import 'dayjs/locale/de';
import 'dayjs/locale/en';

import {
  LANGUAGE,
  LANGUAGE_SWITCHER_MODE_TYPE,
  LANGUAGE_TYPE,
  LOCAL_STORAGE_TYPE,
  mappableCompanyRouteKeys,
} from '@utils/constants';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLanguage, setLanguage } from '../../store/slices/configSlice';
import { Divider, Item, LanguageSwitcherRoot } from './styles';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

interface LanguageItemProps {
  mode: (typeof LANGUAGE_SWITCHER_MODE_TYPE)[number];
  fontWeight?: string;
  hideEN?: boolean;
  hideDE?: boolean;
}
const LanguageSwitcher = ({
  mode,
  fontWeight,
  hideEN = false,
  hideDE = false,
}: LanguageItemProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const language = useSelector(getLanguage);
  const location = useLocation();
  const navigate = useNavigate();

  const valueLanguage = window.localStorage.getItem(
    LOCAL_STORAGE_TYPE.LANGUAGE
  ) as (typeof LANGUAGE_TYPE)[number];
  const [hovered, setHovered] = useState<(typeof LANGUAGE_TYPE)[number]>();

  const pathToRegex = (path: string) => {
    const paramNames: string[] = [];

    const regexPath = path.replace(/:([^/]+)/g, (_, paramName) => {
      paramNames.push(paramName);
      return '([^/]+)';
    });

    return {
      regex: new RegExp(`^${regexPath}/?$`),
      paramNames,
    };
  };

  useEffect(() => {
    if (valueLanguage) setHovered(valueLanguage);
  }, [language, valueLanguage]);

  const handleLanguage = useCallback(
    (targetLang: (typeof LANGUAGE_TYPE)[number]) => {
      let matchedKey: string | null = null;
      const params: Record<string, string> = {};

      for (const key of mappableCompanyRouteKeys) {
        const dePath = i18n.t(`mappable-company-routes.${key}`, { lng: 'de' });
        const enPath = i18n.t(`mappable-company-routes.${key}`, { lng: 'en' });

        for (const path of [dePath, enPath]) {
          const { regex, paramNames } = pathToRegex(path);
          const match = location.pathname.match(regex);

          if (match) {
            matchedKey = key;

            paramNames.forEach((name, i) => {
              params[name] = match[i + 1];
            });

            break;
          }
        }

        if (matchedKey) break;
      }

      window.localStorage.setItem(LOCAL_STORAGE_TYPE.LANGUAGE, targetLang);
      dayjs.locale(targetLang);
      dispatch(setLanguage(targetLang));

      const translatedBase = matchedKey
        ? i18n.t(`mappable-company-routes.${matchedKey}`, {
            lng: targetLang,
          })
        : null;

      let newPath: string | null = null;

      if (translatedBase) {
        newPath = translatedBase.replace(/:([^/]+)/g, (_, paramName) => {
          return params[paramName] || '';
        });
      }

      void i18n.changeLanguage(targetLang).then(() => {
        // void for Lint to ignore Promise
        if (newPath && newPath !== location.pathname) {
          navigate(newPath, { replace: true });
        }
      });
    },
    [dispatch, i18n, location.pathname, navigate]
  );

  return (
    <LanguageSwitcherRoot mode={mode} data-testid="language-switcher">
      {!hideDE && (
        <Item
          $isHovered={hovered === LANGUAGE.GERMAN}
          $fontWeight={fontWeight}
          onMouseOver={() => {
            setHovered(LANGUAGE.GERMAN);
          }}
          onMouseOut={() => {
            setHovered(language);
          }}
          onClick={() => {
            handleLanguage(LANGUAGE.GERMAN);
          }}
        >
          {t(LANGUAGE.GERMAN).toUpperCase()}
        </Item>
      )}

      {!hideDE && !hideEN && <Divider />}

      {!hideEN && (
        <Item
          $isHovered={hovered === LANGUAGE.ENGLISH}
          $fontWeight={fontWeight}
          onMouseOver={() => {
            setHovered(LANGUAGE.ENGLISH);
          }}
          onMouseOut={() => {
            setHovered(language);
          }}
          onClick={() => {
            handleLanguage(LANGUAGE.ENGLISH);
          }}
        >
          {t(LANGUAGE.ENGLISH).toUpperCase()}
        </Item>
      )}
    </LanguageSwitcherRoot>
  );
};

export default LanguageSwitcher;
